import { TableauModule } from './types';

export const ENHANCED_TABLEAU_FUNDAMENTALS_MODULE_1: TableauModule = {
  id: 'tableau-fundamentals-module-1',
  title: 'Introduction to Tableau Desktop',
  description: 'Learn the fundamentals of Tableau Desktop, including the interface, data connections, and basic visualization concepts.',
  content: `
# Introduction to Tableau Desktop

## Overview

Tableau Desktop is a powerful data visualization tool that allows you to create stunning visualizations and interactive dashboards. In this module, you'll learn the fundamentals of working with Tableau Desktop.

## Key Learning Objectives

By the end of this module, you will be able to:
- Navigate the Tableau Desktop interface
- Connect to various data sources
- Create basic visualizations
- Understand fundamental Tableau concepts

## The Tableau Interface

### Main Components

1. **Data Pane**: Shows your data sources and fields
2. **Analytics Pane**: Contains analytics objects for advanced analysis
3. **Pages Shelf**: Creates separate pages for different values
4. **Filters Shelf**: Contains filters that apply to the entire worksheet
5. **Marks Card**: Controls the visual properties of marks in your view

### Connecting to Data

Tableau can connect to numerous data sources:
- Excel files
- CSV files
- Databases (SQL Server, MySQL, PostgreSQL)
- Cloud services (Google Sheets, Salesforce)
- Web data connectors

## Creating Your First Visualization

### Step-by-Step Process

1. **Connect to Data**: Start by connecting to your data source
2. **Drag Fields**: Drag dimensions to Rows/Columns shelf
3. **Add Measures**: Drag measures to create visualizations
4. **Customize**: Use the Marks card to customize appearance

## Best Practices

- Start with simple visualizations
- Consider your audience
- Use appropriate chart types for your data
- Keep visualizations clean and focused

## Exercise: Building a Basic Chart

Try creating a simple bar chart:
1. Connect to the Sample Superstore data
2. Drag Category to Columns
3. Drag Sales to Rows
4. Observe the automatic bar chart creation

## Summary

This module introduced you to Tableau Desktop's interface and basic concepts. Practice connecting to different data sources and creating simple visualizations to build your confidence with the tool.
  `,
  duration: '45 minutes',
  difficulty: 'fundamentals',
  order: 1,
  estimatedTime: '45 minutes',
  objectives: [
    'Navigate the Tableau Desktop interface confidently',
    'Connect to various data sources including Excel and databases',
    'Create basic visualizations using drag-and-drop functionality',
    'Understand the relationship between dimensions and measures',
    'Apply basic formatting and customization to charts'
  ],
  prerequisites: [
    'Basic computer skills',
    'Familiarity with spreadsheet applications',
    'Understanding of basic data concepts'
  ],
  practice: {
    task: 'Create a comprehensive sales dashboard using the Sample Superstore dataset. Your dashboard should include at least 3 different chart types and demonstrate your understanding of Tableau fundamentals.',
    starter: `// Tableau Practice Exercise
// 
// Objective: Create a Sales Analysis Dashboard
// 
// Instructions:
// 1. Connect to Sample Superstore data
// 2. Create the following visualizations:
//    - Bar chart showing Sales by Category
//    - Line chart showing Sales trend over time
//    - Map showing Sales by State
//    - KPI cards for Total Sales, Profit, and Quantity
// 
// Requirements:
// - Use appropriate colors and formatting
// - Add filters for interactivity
// - Ensure visualizations are properly labeled
// 
// Tips:
// - Start with simple charts and build complexity
// - Use the Show Me panel for chart suggestions
// - Apply the Show Me panel for chart suggestions
// - Apply consistent formatting across all charts`,
    solution: `// Tableau Dashboard Solution
// 
// Step-by-step solution:
// 
// 1. Data Connection:
//    - File > Connect > Microsoft Excel
//    - Select Sample Superstore.xlsx
//    - Drag Orders table to canvas
// 
// 2. Sales by Category Bar Chart:
//    - New Worksheet
//    - Drag Category to Columns
//    - Drag Sales to Rows
//    - Sort descending by Sales
//    - Format: Change colors, add data labels
// 
// 3. Sales Trend Line Chart:
//    - New Worksheet  
//    - Drag Order Date to Columns
//    - Drag Sales to Rows
//    - Change mark type to Line
//    - Add trend line: Analytics > Trend Line
// 
// 4. Sales by State Map:
//    - New Worksheet
//    - Double-click State (creates map)
//    - Drag Sales to Color
//    - Adjust color palette for better visibility
// 
// 5. KPI Dashboard:
//    - Create calculated fields for metrics
//    - Use big number format
//    - Add conditional formatting
// 
// 6. Final Dashboard:
//    - New Dashboard
//    - Drag worksheets to dashboard
//    - Add filters and actions
//    - Format for professional appearance
// 
// Advanced Features Used:
// - Calculated fields
// - Parameters for dynamic filtering
// - Dashboard actions for interactivity
// - Custom color palettes
// - Responsive design principles`,
    hints: [
      'Use the Show Me panel to explore different chart types automatically',
      'Double-clicking geographic fields automatically creates maps',
      'Hold Ctrl while dragging to copy fields instead of moving them',
      'Use the Analytics pane to add trend lines, reference lines, and forecasts',
      'Right-click on fields to access formatting and calculation options',
      'Create calculated fields for custom metrics and KPIs',
      'Use dashboard actions to create interactivity between visualizations'
    ],
    estimatedTime: 60
  },
  quiz: {
    questions: [
      {
        id: 'tableau-fund-q1',
        question: 'Which of the following is NOT a main component of the Tableau Desktop interface?',
        options: [
          'Data Pane',
          'Analytics Pane',
          'Calculation Editor',
          'Marks Card'
        ],
        correctAnswer: 2,
        difficulty: 'easy',
        explanation: 'The Calculation Editor is a dialog box that appears when creating calculated fields, but it is not a permanent component of the main Tableau interface like the Data Pane, Analytics Pane, and Marks Card.',
        points: 1,
        type: 'multiple-choice'
      },
      {
        id: 'tableau-fund-q2',
        question: 'What is the difference between dimensions and measures in Tableau?',
        options: [
          'Dimensions are numeric, measures are categorical',
          'Dimensions are categorical, measures are numeric',
          'Dimensions are blue, measures are green',
          'There is no difference between dimensions and measures'
        ],
        correctAnswer: 1,
        difficulty: 'easy',
        explanation: 'Dimensions are categorical fields (like Category, Region, Customer Name) that are typically discrete and used to slice and dice data. Measures are numeric fields (like Sales, Profit, Quantity) that are typically continuous and can be aggregated.',
        points: 1,
        type: 'multiple-choice'
      },
      {
        id: 'tableau-fund-q3',
        question: 'Which shelf would you use to create separate pages for different values in your visualization?',
        options: [
          'Filters Shelf',
          'Pages Shelf',
          'Rows Shelf',
          'Columns Shelf'
        ],
        correctAnswer: 1,
        difficulty: 'medium',
        explanation: 'The Pages Shelf is used to create separate pages for different values of a field, allowing you to create animated visualizations or step through different views of your data.',
        points: 2,
        type: 'multiple-choice'
      },
      {
        id: 'tableau-fund-q4',
        question: 'True or False: Tableau can only connect to Excel files and CSV files.',
        options: [
          'True',
          'False'
        ],
        correctAnswer: 1,
        difficulty: 'easy',
        explanation: 'False. Tableau can connect to numerous data sources including Excel, CSV, various databases (SQL Server, MySQL, PostgreSQL), cloud services (Google Sheets, Salesforce), web data connectors, and many more.',
        points: 1,
        type: 'true-false'
      },
      {
        id: 'tableau-fund-q5',
        question: 'What is the best practice when starting to create visualizations in Tableau?',
        options: [
          'Start with the most complex visualization first',
          'Always use the same chart type for consistency',
          'Start with simple visualizations and build complexity',
          'Use as many colors as possible to make it attractive'
        ],
        correctAnswer: 2,
        difficulty: 'medium',
        explanation: 'It is best practice to start with simple visualizations and gradually build complexity. This approach helps you understand your data better and ensures your visualizations effectively communicate insights.',
        points: 2,
        type: 'multiple-choice'
      }
    ],
    passThreshold: 70
  }
};

// Add enhanced quiz content for the expert module
export const ENHANCED_TABLEAU_EXPERT_MODULE: TableauModule = {
  id: 'advanced-analytics-integration',
  title: 'Advanced Analytics with R & Python',
  description: 'Integrate statistical computing and machine learning models with Tableau visualizations.',
  content: `
# Advanced Analytics with R & Python Integration

## Overview of Analytics Integration

### TabPy (Tableau Python Server)
TabPy enables Tableau to execute Python scripts and return results for visualization.

#### Installation and Setup
\`\`\`bash
# Install TabPy
pip install tabpy

# Start TabPy server
tabpy

# Default URL: http://localhost:9004
\`\`\`

#### Tableau Configuration
1. **Help > Settings and Performance > Manage Analytics Extension Connection**
2. **Server**: localhost
3. **Port**: 9004
4. **Sign in required**: No (for local development)

### Rserve Integration
Similar setup for R integration using Rserve.

## Python Integration Examples

### Statistical Analysis

#### Correlation Analysis
\`\`\`python
# Tableau calculated field using SCRIPT_REAL
SCRIPT_REAL("
import pandas as pd
import numpy as np
from scipy.stats import pearsonr

# Get data from Tableau
sales = _arg1
profit = _arg2

# Calculate correlation
correlation, p_value = pearsonr(sales, profit)
return correlation
", 
SUM([Sales]), SUM([Profit]))
\`\`\`

## Advanced Analytics Workflows

Expert-level analytics integration requires understanding of both Tableau and programming languages to create sophisticated analytical solutions.
  `,
  duration: '4 hours',
  difficulty: 'advanced',
  order: 1,
  estimatedTime: '4 hours',
  objectives: [
    'Configure and use TabPy for Python integration',
    'Implement machine learning models in Tableau workflows',
    'Build advanced statistical analysis dashboards',
    'Design enterprise analytics deployment strategies',
    'Monitor and maintain analytics extensions in production'
  ],
  quiz: {
    questions: [
      {
        id: 'tableau-expert-q1',
        question: 'What is the default port for TabPy server?',
        options: [
          '8080',
          '9004',
          '3000',
          '5432'
        ],
        correctAnswer: 1,
        difficulty: 'medium',
        explanation: 'TabPy (Tableau Python Server) runs on port 9004 by default when started locally.',
        points: 2,
        type: 'multiple-choice'
      },
      {
        id: 'tableau-expert-q2',
        question: 'Which function prefix is used for Python scripts that return real numbers in Tableau?',
        options: [
          'SCRIPT_STR',
          'SCRIPT_INT',
          'SCRIPT_REAL',
          'SCRIPT_BOOL'
        ],
        correctAnswer: 2,
        difficulty: 'medium',
        explanation: 'SCRIPT_REAL is used when Python scripts return floating-point numbers, while SCRIPT_STR returns strings and SCRIPT_INT returns integers.',
        points: 2,
        type: 'multiple-choice'
      },
      {
        id: 'tableau-expert-q3',
        question: 'True or False: LOD expressions can be used in combination with Python scripts for advanced analytics.',
        options: [
          'True',
          'False'
        ],
        correctAnswer: 0,
        difficulty: 'hard',
        explanation: 'True. LOD expressions can be combined with Python scripts to create sophisticated analytics by controlling the level of detail at which calculations are performed before passing data to Python.',
        points: 3,
        type: 'true-false'
      }
    ],
    passThreshold: 80
  }
};
